import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spin, Modal, Input, message } from 'antd';
import { listarHomologacoesPorCliente, inativarHomologacao, buscarLimiteHomologacoes } from '../api/homologacao';
import ChatPendencias from '../components/ChatPendencias';
import { useChatPendencias } from '../hooks/useChatPendencias';

const STATUS_COLOR: Record<string, string> = {
  rascunho: '#888',
  finalizado: '#16a34a',
  enviado: '#2563eb',
  aprovado: '#7c3aed',
  reprovado: '#dc2626',
};

export default function HomologacoesListPage() {
  const { clienteId } = useParams<{ clienteId: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [limiteAtivas, setLimiteAtivas] = useState(1);
  const [inativandoId, setInativandoId] = useState<number | null>(null);
  const [motivoInativacao, setMotivoInativacao] = useState('');
  const [inativando, setInativando] = useState(false);

  // Hook de tempo real das pendências (conecta socket, escuta eventos, mantém lista)
  const { lista: pendList, refresh: refreshPend, setLista: setPendList, connected: pendConnected } = useChatPendencias(
    clienteId ? Number(clienteId) : null
  );

  const carregar = () => {
    if (!clienteId) return;
    setLoading(true);
    Promise.all([
      listarHomologacoesPorCliente(Number(clienteId)),
      buscarLimiteHomologacoes(Number(clienteId)),
    ])
      .then(([lista, limite]) => { setData(lista); setLimiteAtivas(limite); })
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { carregar(); }, [clienteId]);

  const ativasCount = data.filter((h) => h.ativa === 1 || h.ativa === undefined).length;
  const temAtiva = ativasCount >= limiteAtivas;

  const confirmarInativacao = async () => {
    if (!inativandoId) return;
    setInativando(true);
    try {
      await inativarHomologacao(inativandoId, motivoInativacao.trim() || undefined);
      message.success('Homologação inativada');
      setInativandoId(null);
      setMotivoInativacao('');
      carregar();
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Erro ao inativar');
    } finally {
      setInativando(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <Spin size="large" tip="Carregando..." />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5', padding: '40px 16px 32px' }}>
      <p style={{ fontSize: 12, color: '#999', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1 }}>
        Cliente {clienteId}
      </p>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111', margin: 0 }}>Homologações</h2>
        {temAtiva ? (
          <span style={{ fontSize: 12, color: '#888', fontStyle: 'italic' }}>
            {limiteAtivas > 1
              ? 'Inative uma para criar outra (máx. 2 ativas)'
              : 'Inative a atual para criar uma nova'}
          </span>
        ) : (
          <button
            onClick={() => navigate(`/homologacao/novo/${clienteId}`)}
            style={{ padding: '10px 18px', background: '#111', color: '#fff', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          >
            + Nova
          </button>
        )}
      </div>

      {/* Layout em coluna única: lista de homologações em cima, chat embaixo.
          Funciona bem em iframe estreito do Pipefy. */}
      {data.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#aaa', marginTop: 60, fontSize: 14 }}>
          Nenhuma homologação encontrada
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {data.map((h) => {
            const isInativa = h.ativa === 0;
            return (
              <div
                key={h.id}
                style={{
                  background: isInativa ? '#fafafa' : '#fff',
                  borderRadius: 12,
                  padding: '16px 20px',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  opacity: isInativa ? 0.7 : 1,
                }}
              >
                <div
                  onClick={() => navigate(`/homologacao/detalhes/${h.id}`)}
                  style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0, flex: 1, cursor: 'pointer' }}
                >
                  <span style={{ fontSize: 15, fontWeight: 600, color: isInativa ? '#666' : '#111', textDecoration: isInativa ? 'line-through' : 'none' }}>
                    {h.potencia ? `${h.potencia} kWp` : `Homologação #${h.id}`}
                  </span>
                  <span style={{ fontSize: 12, color: '#aaa' }}>
                    {new Date(h.created_at).toLocaleDateString('pt-BR')}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  {isInativa ? (
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#999', background: '#eee', borderRadius: 20, padding: '4px 10px', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      inativada
                    </span>
                  ) : (
                    <>
                      <span style={{ fontSize: 11, fontWeight: 600, color: STATUS_COLOR[h.status] || '#888', background: '#f5f5f5', borderRadius: 20, padding: '4px 10px', whiteSpace: 'nowrap', textTransform: 'capitalize' }}>
                        {h.status}
                      </span>
                      <button
                        onClick={(e) => { e.stopPropagation(); setInativandoId(h.id); }}
                        style={{ padding: '4px 10px', background: '#fff', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
                        title="Inativar homologação"
                      >
                        Inativar
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ marginTop: 20 }}>
        <ChatPendencias
          clienteId={Number(clienteId)}
          pendencias={pendList}
          onChange={setPendList}
          onCreated={refreshPend}
          connected={pendConnected}
        />
      </div>

      <Modal
        title="Inativar homologação"
        open={inativandoId !== null}
        onOk={confirmarInativacao}
        onCancel={() => { if (!inativando) { setInativandoId(null); setMotivoInativacao(''); } }}
        okButtonProps={{ danger: true, loading: inativando }}
        okText="Inativar"
        cancelText="Cancelar"
      >
        <p style={{ marginBottom: 12 }}>
          Ao inativar, esta homologação deixa de ser ativa e libera a criação de uma nova para o mesmo cliente.
          O registro e os arquivos não são apagados — ficam disponíveis para consulta.
        </p>
        <Input.TextArea
          placeholder="Motivo (opcional)"
          value={motivoInativacao}
          onChange={(e) => setMotivoInativacao(e.target.value)}
          rows={3}
        />
      </Modal>
    </div>
  );
}
